/**
 * Host for imperative `showAlert` dialogs on all platforms.
 * Uses the shared AlertDialog UI.
 */
import { useEffect, useRef, useState } from "react";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Text } from "@/components/ui/text";
import {
    subscribeAlert,
    type AppAlertButton,
    type AppAlertRequest,
} from "@/lib/showAlert";

export function AlertDialogHost() {
    const [request, setRequest] = useState<AppAlertRequest | null>(null);
    const actionTakenRef = useRef(false);

    useEffect(() => {
        return subscribeAlert((next) => {
            actionTakenRef.current = false;
            setRequest(next);
        });
    }, []);

    const open = request != null;

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen || !request) {
            return;
        }

        const dismissed = request;
        setRequest(null);

        // Backdrop / Escape dismiss — run cancel if no button handled the close.
        if (!actionTakenRef.current) {
            const cancel = dismissed.buttons.find(
                (button) => button.variant === "outline"
            );
            void cancel?.onPress?.();
        }
        actionTakenRef.current = false;
    };

    const runButton = (button: AppAlertButton) => {
        actionTakenRef.current = true;
        void button.onPress?.();
    };

    const cancelButtons = (request?.buttons ?? []).filter(
        (button) => button.variant === "outline"
    );
    const actionButtons = (request?.buttons ?? []).filter(
        (button) => button.variant !== "outline"
    );

    return (
        <AlertDialog open={open} onOpenChange={handleOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{request?.title}</AlertDialogTitle>
                    {request?.message ? (
                        <AlertDialogDescription>
                            {request.message}
                        </AlertDialogDescription>
                    ) : null}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    {cancelButtons.map((button, index) => (
                        <AlertDialogCancel
                            key={`cancel-${button.text}-${index}`}
                            onPress={() => runButton(button)}
                            testID={button.testID}
                        >
                            <Text>{button.text}</Text>
                        </AlertDialogCancel>
                    ))}
                    {actionButtons.map((button, index) => (
                        <AlertDialogAction
                            key={`action-${button.text}-${index}`}
                            variant={button.variant ?? "default"}
                            onPress={() => runButton(button)}
                            testID={button.testID}
                        >
                            <Text>{button.text}</Text>
                        </AlertDialogAction>
                    ))}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
